import { Mol } from '@moleculeone/types';
import React, { useMemo } from 'react';

import { styled } from '../../../../stitches.config';
import { Button } from '../../../controls/Button/Button';
import { ButtonCopy } from '../../../controls/Button/ButtonCopy';
import { Icon } from '../../../text/Icon/Icon';
import { SanitizedHTML } from '../../../text/SanitizedHTML/SanitizedHTML';
import { ReferenceReaction } from '../utils/referenceReactions';

import { ReactionPreview } from './ReactionPreview';

const ReferenceContainer = styled('div', {
  backgroundColor: 'white',
  borderRadius: '$small',
  width: '100%',
});

const ReactionContext = styled('div', {
  padding: '$3',
});

const ToolbarContext = styled('div', {
  padding: '$3',
  borderRadius: '$small',
  backgroundColor: '$slate2',
  display: 'flex',
  gap: '$3',
});

const PatentContext = styled('div', {
  maxHeight: '300px',
  overflowY: 'auto',
  backgroundColor: '$slate2',
  padding: '$4',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '$3',
});

const Patent = styled('div', {
  borderRadius: '$small',
  backgroundColor: 'white',
  padding: '$3',
});

const PatentTitle = styled('p', {
  marginRight: '1.5rem',
  marginBottom: '0.5rem',
  fontWeight: 600,
  color: '#5c7080',
});

const PatentContent = styled('div', {
  marginBottom: '0.5rem',
  color: '#8a9ba8',
});

const PatentLink = styled('a', {
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '$2',
  fontSize: '$1',
});

interface ReferenceProps {
  reaction: ReferenceReaction;
  smilesToMol?: (smiles: string) => Mol;
  openInSciFinder?: () => void;
}

export function Reference({ reaction, smilesToMol, openInSciFinder }: ReferenceProps) {
  const [isOpen, setOpen] = React.useState(false);

  const isSciFinderLink = useMemo(
    () => reaction.reference.some(ref => ref.paragraph.startsWith('multistep-reaction/')),
    [reaction]
  );

  const reactionSmiles = `${reaction.substrates.join('.')}>>${reaction.product}`;

  const onTogglePatent = () => {
    setOpen(!isOpen);
  };

  return (
    <ReferenceContainer>
      <ReactionContext>
        <ReactionPreview reaction={reaction} smilesToMol={smilesToMol} />
      </ReactionContext>

      <ToolbarContext>
        {!isSciFinderLink && (
          <Button
            weight="minimal"
            text={isOpen ? 'Hide patent' : 'Show patent'}
            icon={isOpen ? 'close' : 'library_books'}
            onClick={onTogglePatent}
          />
        )}

        {isSciFinderLink && openInSciFinder && (
          <Button
            text="Open in SciFinder"
            icon="menu_book"
            size="small"
            theme="purple"
            onClick={() => {
              openInSciFinder();
            }}
          />
        )}

        <ButtonCopy copy={reactionSmiles} />
      </ToolbarContext>

      {isOpen && (
        <PatentContext>
          {reaction.reference.map((patentRef, i) => {
            const { content, patent, paragraph } = patentRef;

            return (
              <Patent key={`${patent}${paragraph}`}>
                <PatentTitle>
                  {patent}
                  {', § '}
                  {paragraph}
                </PatentTitle>

                <PatentContent>
                  <SanitizedHTML html={content} />
                </PatentContent>

                <PatentLink
                  href={`https://patents.google.com/patent/${patent}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon icon="school" />
                  <span>See more on Google Patents</span>
                </PatentLink>
              </Patent>
            );
          })}
        </PatentContext>
      )}
    </ReferenceContainer>
  );
}
